import { useNotificationContext } from "@circle-react-shared/Notifications/NotificationContext";
import {
  useNotificationsCount,
  useResetNotificationCount,
} from "@circle-react-shared/Notifications/useHeaderNotificationsApi";
import { useNotificationsWidth } from "@circle-react-shared/Notifications/useNotificationsWidth";
import { PopoverPortal as UIKitPopoverPortal } from "@circle-react-shared/uikit/PopoverPortal";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useNotificationWebSocket } from "@circle-react/hooks/notifications/useNotificationWebSocket";
import { useCallback } from "react";
import { useQueryClient } from "react-query";

import { DEFAULT_POPPER_OPTIONS } from "../constants";
import { Banner } from "./Banner";
import { Body } from "./Body";
import { Header } from "./Header";
import { NotificationPopoverButton } from "./NotificationPopoverButton";
import {
  onNotificationReceive,
  type WebSocketNotificationEvent,
} from "./utils";

export const NotificationPopover = () => {
  const {
    activeFilter,
    notificationCount,
    setNotificationCount,
    setNotificationTabCounters,
  } = useNotificationContext();

  const queryClient = useQueryClient();
  const {
    currentCommunityMember,
    currentCommunity,
    isViewOnlyMasquerading,
    currentCommunitySettings,
  } = usePunditUserContext();
  const isNotificationsTabV2Enabled = Boolean(
    currentCommunitySettings?.live_pulse_notifications_tab_v2_enabled,
  );

  useNotificationsCount(currentCommunity?.id, setNotificationTabCounters);
  const { mutate: resetNotificationCount } =
    useResetNotificationCount(setNotificationCount);

  useNotificationWebSocket(
    currentCommunityMember?.id as number,
    (eventData: WebSocketNotificationEvent) =>
      onNotificationReceive({
        eventData,
        queryClient,
        setNotificationCount,
        isNotificationsTabV2Enabled,
        activeFilter,
      }),
  );

  const handleNotificationPopoverOpen = useCallback(() => {
    if (notificationCount > 0) resetNotificationCount();
  }, [notificationCount, resetNotificationCount]);

  const { tabsContainerRef, tabsContainerWidth } = useNotificationsWidth();

  return (
    <UIKitPopoverPortal
      renderButton={isOpen => (
        <NotificationPopoverButton
          isOpen={isOpen}
          notificationCount={notificationCount}
        />
      )}
      className={classNames(
        "border-primary bg-primary h-full max-h-[38rem] w-fit max-w-[48rem] rounded-md border shadow-lg overflow-hidden",
        {
          "cursor-not-allowed": isViewOnlyMasquerading,
        },
      )}
      onOpen={handleNotificationPopoverOpen}
      popperOptions={DEFAULT_POPPER_OPTIONS}
      shouldHideOnMdScreens={false}
    >
      <div
        className={classNames("h-full w-fit", {
          "pointer-events-none": isViewOnlyMasquerading,
        })}
        data-testid="notifications-drawer"
      >
        <Banner tabsContainerWidth={tabsContainerWidth} />
        <Header />
        <Body
          tabsContainerRef={tabsContainerRef}
          tabsContainerWidth={tabsContainerWidth}
        />
      </div>
    </UIKitPopoverPortal>
  );
};
